import * as React from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { NewModal } from 'lib/components';
import { useAuth, VideoListAutomotiveItem } from 'lib/context';
import { Button } from 'react-covideo-common';
import {
  DEFAULT_FOLDER,
  DefaultFolderIds,
  SHARED_FOLDER_PREFIX,
} from 'lib/const/Folders';
import { useVideoMoveCopy } from 'lib/api/videos/useVideoMoveCopy';
import { useVideosMoveCopyMultipleMutation } from 'lib/api/videos/useVideoMoveCopyMultiple';
import { FoldersV2 } from 'lib/api/folders/getFoldersV2';
import {
  ACCESS_FILTER,
  FolderV2Dropdown,
} from '../dropdown/folders/FolderV2Dropdown';
import { Formik } from 'formik';
import { FormikCheckboxField } from '../formik/FormikCheckbox';
import { Flex } from '../styles/layout';
import { trackVideoActionEvent } from 'lib/utils/pushToDataLayer';

const Title = styled.div`
  height: 24px;
  opacity: 0.75;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.71;
  letter-spacing: 0.4px;
  color: #343a40;
  margin-bottom: 8px;
`;

type Props = {
  handleModalClose: () => void;
  videoIds: string[];
  currentFolder?: FoldersV2;
  folder?: FoldersV2;
  containsSharedFolderVideos: boolean;
  selectedVideo: VideoListAutomotiveItem | null;
};

export const ModalMoveCopy = (props: Props) => {
  const { handleModalClose, videoIds, currentFolder, folder } = props;

  const {
    userData,
    userData: { resellerId, id: userId },
  } = useAuth();
  const walkaroundSocialFolder = ['204'];

  const { mutateAsync } = useVideoMoveCopy();
  const { colors } = useTheme();

  const { mutateAsync: mutateMultiple } = useVideosMoveCopyMultipleMutation();

  const handleSubmit = async (values: { copy: boolean; folder: FoldersV2 }) => {
    if (!videoIds.length) {
      return;
    }
    const { copy, folder: selectedFolder } = values;
    const isFolderShared = selectedFolder.userId?.toString() !== userId;

    const obj = {
      copy,
      videoFolder: isFolderShared
        ? `${SHARED_FOLDER_PREFIX}${selectedFolder.folderId}`
        : selectedFolder.name,
      successMessage: `Video ${copy ? 'copied' : 'moved'} successfully.`,
    };
    try {
      let ids: number[] = [];
      if (videoIds.length > 1) {
        for (let i = 0; i < videoIds.length; i++) {
          const id = parseInt(videoIds[i]);
          if (!isNaN(id)) ids.push(id);
        }
        await mutateMultiple({
          ...obj,
          ids,
          successMessage: `Videos ${copy ? 'copied' : 'moved'} successfully.`,
        });
      } else {
        await mutateAsync({
          ...obj,
          videoId: videoIds[0],
        });
        trackVideoActionEvent({
          event: 'Video Interaction',
          videoAction: copy ? 'Copy' : 'Move',
          video: props.selectedVideo as VideoListAutomotiveItem,
          userData,
        });
      }

      handleModalClose();
    } catch (err) {
      console.log('err', err);
    }
  };

  const getMoveFieldValues = (selectedValue?: FoldersV2) => {
    if (selectedValue) {
      if (
        selectedValue.name === 'Walkaround Social' &&
        walkaroundSocialFolder.includes(resellerId)
      ) {
        return {
          copy: false,
          folder: selectedValue,
        };
      }

      if (
        selectedValue.name === 'Company' ||
        currentFolder?.folderId === DefaultFolderIds.COMPANY
      ) {
        return {
          copy: true,
          folder: selectedValue,
        };
      }

      return {
        copy: false,
        folder: selectedValue,
      };
    }

    return {
      copy: false,
      folder: { ...DEFAULT_FOLDER, userId: parseInt(userId, 10) },
    };
  };

  const getKeepCopyCondition = (values: {
    copy: boolean;
    folder?: FoldersV2;
  }) => {
    if (folder?.name === 'Company') {
      return { disabled: true, keepCopy: true };
    }

    if (currentFolder?.folderId === DefaultFolderIds.COMPANY) {
      return { disabled: true, keepCopy: true };
    }

    if (folder?.name === 'Vault' || currentFolder?.name === 'Vault') {
      return { disabled: true, keepCopy: false };
    }
    return { disabled: false, keepCopy: values.copy };
  };
  return (
    <NewModal
      hideHeader={true}
      closeModalOnBackdropOrEscPress={false}
      closeModal={() => {
        handleModalClose();
      }}
      style={{
        content: {
          padding: '24px 24px 16px 24px',
          maxWidth: '352px',
          minWidth: 'auto',
          height: '230px',
        },
      }}
    >
      <Formik
        initialValues={getMoveFieldValues(folder)}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values, setFieldValue, submitForm }) => (
          <Flex justifyContent='space-between' height='100%'>
            <Title>Select Folder:</Title>
            <FolderV2Dropdown
              value={{
                value: values.folder?.folderId ?? DEFAULT_FOLDER.folderId,
                label: values.folder?.name ?? DEFAULT_FOLDER.name,
              }}
              showAllFolders={false}
              showCreateButton={true}
              accessFilter={ACCESS_FILTER.EDIT}
              dropdownHeight={140}
              containerWidth='100%'
              onChange={(_, folder) => {
                if (folder) {
                  const fieldValues = getMoveFieldValues(folder);
                  setFieldValue('copy', fieldValues?.copy);
                  setFieldValue('folder', folder);
                }
              }}
            />
            <Flex margin='16px 0'>
              <FormikCheckboxField
                name='copy'
                label='Keep a copy of this video in the current folder'
                onChange={() => {
                  setFieldValue(`copy`, !values.copy);
                }}
                disabled={isSubmitting || getKeepCopyCondition(values).disabled}
                extendStyles={{
                  labelStyles: {
                    fontSize: '15px',
                    fontWeight: 400,
                    color: colors.neutral[80],
                    lineHeight: '24px',
                    paddingLeft: '12px',
                  },
                }}
              />
            </Flex>
            <Flex
              justifyContent='flex-end'
              gap='8px'
              flexDirection='row'
              padding='16px 0 0 0'
            >
              <Button
                text='Cancel'
                variant='secondary'
                disabled={isSubmitting}
                onClick={() => {
                  handleModalClose();
                }}
              />
              <Button
                variant='primary'
                text={
                  getKeepCopyCondition(values).keepCopy
                    ? 'Copy Video'
                    : 'Move Video'
                }
                disabled={isSubmitting}
                type='submit'
                onClick={submitForm}
              />
            </Flex>
          </Flex>
        )}
      </Formik>
    </NewModal>
  );
};
